/**
 * defa.behaviors.FormEnabler
 * Enables and disables parts of the forms based on filled fields.
 * @version 1.0.9
 */
import { T } from 'defa-object';

const selectors = {
	inputRequired     : '[required]',
	inputAll          : '[data-input]',
	textAll           : '[data-text]',
	buttonCancel      : '[data-click$=-cancel]',
	buttonClose       : '[data-click$=-close]',
	buttonSubmit      : '[data-click$=-submit]',
	feedback          : '.static',
	containerArrays   : '[data-container$=-array]',
	requiredEqualValue: '[data-validate-equal]',
};

const FormEnabler = Mn.Behavior.extend( {
	
	// ------------------------------------------------
	// Setup
	// ------------------------------------------------
	
	ui: {
		inputs  : selectors.inputAll,
		texts   : selectors.textAll,
		required: selectors.inputRequired,
		cancel  : selectors.buttonCancel,
		close   : selectors.buttonClose,
		submit  : selectors.buttonSubmit,
	},
	
	events: {
		'input         @ui.inputs': 'onInputInputs',
		'change        @ui.inputs': 'onInputInputs',
		'transitionend @ui.inputs': 'onTransitionEndInputs',
		'keypress      @ui.inputs': 'onKeypressInputs',
		'click         @ui.submit': 'onClickSubmit',
		'click         @ui.cancel': 'onClickCancel',
		'click         @ui.close' : 'onClickClose',
		'blur          @ui.inputs': 'onBlurInputs',
	},
	
	modelEvents: {
		'success': 'onModelSuccess',
		'sync'   : 'onModelSuccess',
		'error'  : 'onModelError',
	},
	
	
	// ------------------------------------------------
	// Event Handlers
	// ------------------------------------------------
	
	// lifecycle event handlers
	
	/**
	 * @constructor
	 */
	initialize: function () {
		this.view.isRequiredFilled  = this.isRequiredFilled;
		this.view.isArraysValidated = this.isArraysValidated;
		this.view.removeErrors      = this.removeErrors;
		this.view.resetRequired     = this.resetRequired;
		this.view.outputFeedback    = this.outputFeedback;
	},
	
	/**
	 * Triggers when the view attaches to the DOM.
	 * @void
	 */
	onRender: function () {
		if ( !this.view.noInitValidation ) {
			this._verifyForm();
		}
	},
	
	// @ui event handlers
	
	/**
	 * Enables and disables the submit button based on if all required fields are filled.
	 * @void
	 */
	onInputInputs: function ( event ) {
		
		if ( Mn.getClosestCid( event.target ) !== this.view.cid ) {
			return;
		}
		
		this._verifyForm();
	},
	
	/**
	 * Enables and disables the submit button based on if all required fields are filled.
	 * Note: this triggers only when google changes the background color of the element (aka autofill completed )
	 * @void
	 */
	onTransitionEndInputs: function ( event ) {
		
		if ( Mn.getClosestCid( event.target ) !== this.view.cid ) {
			return;
		}
		
		this.doChange();
	},
	
	/**
	 * Debounced trigger for google transitionEnd background color element.
	 * @void
	 */
	doChange: _.debounce( function () {
		this.ui.submit.removeAttr( 'disabled' );
		
	}, 16 ),
	
	/**
	 * Allows the enter keys to be used to call the view's submit function.
	 * @void
	 */
	onKeypressInputs: function ( event ) {

		if ( Mn.getClosestCid( event.target ) !== this.view.cid ) {
			return;
		}
		
		if ( !this.view.noEnterKeypress ) {
			
			let ENTER_KEY = 13;
			
			if ( event.which === ENTER_KEY && this.isRequiredFilled() && !event.shiftKey ) {
				
				event.preventDefault();
				
				this.ui.submit.focus();
				this._submit();
			}
		}
		
	},
	
	// @ui event handlers
	
	/**
	 * Triggers when the end user clicks the cancel button.
	 * @void
	 */
	onClickCancel: function ( event ) {
		event.preventDefault();
		
		if ( Mn.getClosestCid( event.target ) !== this.view.cid ) {
			return;
		}
		
		this.view.trigger( 'cancel', this.view );
	},
	
	/**
	 * Triggers when the end user clicks the close button.
	 * @void
	 */
	onClickClose: function ( event ) {
		event.preventDefault();
		
		if ( Mn.getClosestCid( event.target ) !== this.view.cid ) {
			return;
		}
		
		this.view.trigger( 'close', this.view );
	},
	
	/**
	 * Triggers the view's _submit function when the submit button is clicked.
	 * @void
	 */
	onClickSubmit: function ( event ) {
		event.preventDefault();
		
		if ( Mn.getClosestCid( event.target ) !== this.view.cid ) {
			return;
		}
		
		this._submit();
	},
	
	/**
	 * Marks a field as visited after blur event - so we can visibly validate only visited fields.
	 *
	 * Used for fields which must be equal.
	 */
	onBlurInputs: function ( event ) {
		if ( Mn.getClosestCid( event.target ) !== this.view.cid ) {
			return;
		}
		$( event.target ).prop( 'data-visited', true );
		this._verifyForm();
	},
	
	// model event handlers
	
	/**
	 * Triggers when a model updates.
	 * @void
	 */
	onModelSuccess: function ( model, response ) {
		
		if ( !this.view.noSuccessFeedback ) {
			this.outputFeedback( {
				response: response,
				status  : 'success',
			} );
		}
		
	},
	
	/**
	 * Triggers when a model fails.
	 * @void
	 */
	onModelError: function ( model, response ) {
		
		this.outputFeedback( {
			response: response,
			status  : 'error',
		} );
	},
	
	
	// ------------------------------------------------
	// Other functions
	// ------------------------------------------------
	
	/**
	 * Verifies form-integrity and enables/disables the submit buttons.
	 * @void
	 */
	_verifyForm: function () {
		this.removeFeedback();
		if ( _.isFunction( this.view.verifyForm ) ) {
			this.view.verifyForm();
		}
		
		if ( this._validate() ) {
			if ( _.isFunction( this.view.enableSubmit ) ) {
				this.view.enableSubmit();
			}
		} else {
			if ( _.isFunction( this.view.disableSubmit ) ) {
				this.view.disableSubmit();
			}
		}
	},
	
	/**
	 * Combined functions validation boolean.
	 * @return {bool} True if all validation functions return true, false if not.
	 */
	_validate: function () {
		
		let val = true;
		
		if ( !this.isRequiredFilled() ) {
			val = false;
		}
		
		if ( !this.isArraysValidated() ) {
			val = false;
		}
		
		if ( !this.isRequiredEqualValueEqual() ) {
			val = false;
		}
		
		if ( _.isFunction( this.view.validate ) && !this.view.validate() ) {
			val = false;
		}
		
		return val;
		
	},
	
	
	// feedback control
	
	/**
	 * Outputs a new feedback-message.
	 * @param {object}  options             Object in control of the feedback response.
	 *                  options.response    Object containing a message string, or a responseJSON containing a
	 *     message string. options.status      String containing the kind of feedback, normally success or error.
	 *     options.submit      String containing what to do with the submit-button. defaults to "hide".
	 * @void
	 */
	outputFeedback: function ( options ) {
		
		options        = options || {};
		this.view      = this.view || this;
		options.submit = this.view.submitReaction || 'hide';
		
		this.view.idle();
		
		if ( options.response ) {
			
			// Print Message
			let message = ( options.response.responseJSON )
						  ? options.response.responseJSON.message
						  : options.response.message;
			
			
			if ( message ) {
				let html5 = '<p role="alert" class="input static %s1">%s2</p>'
					.replace( '%s1', options.status )
					.replace( '%s2', message );
				
				if ( !this.ui.inputs ) {
					this.ui.inputs = this.$el.find( selectors.inputAll );
				}
				
				this.ui.inputs.parents( 'fieldset:visible:last' ).append( html5 );
			}
			
			// Change Submit
			if ( 'success' === options.status && 'string' === typeof options.submit ) {
				switch ( options.submit ) {
					
					case 'hide' :
						if ( _.isFunction( this.view.hideSubmit ) ) {
							this.view.hideSubmit();
						}
						break;
					
					case 'close' :
						if ( _.isFunction( this.view.closeSubmit ) ) {
							this.view.closeSubmit();
						}
						break;
					
					case 'disable' :
						if ( _.isFunction( this.view.disableSubmit ) ) {
							this.view.disableSubmit();
						}
						break;
				}
			}
		}
	},
	
	/**
	 * Removes dynamic errors permanently from the DOM.
	 * @void
	 */
	removeFeedback: function () {
		let dynamicFeedbacks = this.view.$el.find( selectors.feedback )
								   .not( selectors.textAll )
								   .not( '.fade-out' )
								   .not( '.success' );
		
		if ( 1 <= dynamicFeedbacks.length ) {
			
			dynamicFeedbacks.addClass( 'fade-out' );
			
			let self = this;
			setTimeout( function () {
				self.destroyFeedback( {
					feedbacks: dynamicFeedbacks,
				} );
			}, 1000 );
		}
	},
	
	/**
	 * Destroys static text in the current form.
	 * @param {object} options  jQuery object of feedback-nodes to destroy.
	 * @void
	 */
	destroyFeedback: function ( options ) {
		options       = options || {};
		let feedbacks = options.feedbacks || this.view.$el.find( selectors.feedback ).not( selectors.textAll );
		
		feedbacks.remove();
	},
	
	
	//  validation control
	
	/**
	 * Checks if all required fields are filled.
	 * @returns {bool} True if all required fields are filled, false if not.
	 */
	isRequiredFilled: function () {
		this.resetRequired();
		
		return !_.any( this.ui.required, function ( element ) {
			return !$( element ).val().trim();
		} );
	},
	
	/**
	 * Checks if each container listed as an array has at least one checked item.
	 * @returns {bool} True if no array has any unselected options, false if not.
	 */
	isArraysValidated: function () {
		if ( 0 >= this.$el.find( selectors.containerArrays ).length ) {
			return true;
		} else {
			return _.any( this.$el.find( selectors.containerArrays ), function ( arrayContainer ) {
				let $inputs = $( arrayContainer ).find( selectors.inputAll );
				
				return _.any( $inputs, function ( input ) {
					let $input = $( input );
					// if checkbox or radio
					if ( 'checkbox' === $input.attr( 'type' ) || 'radio' === $input.attr( 'type' ) ) {
						return $input.is( ':checked' );
						// if other kind of input
					} else {
						return $input.val().trim();
					}
				} );
			} );
		}
	},
	
	isRequiredEqualValueEqual: function () {
		let self          = this;
		let valid         = true;
		let validateError = null;
		
		this.$el.find( selectors.requiredEqualValue ).each( function () {
			let element = $( this );
			if ( !element.hasClass( 'disabled' ) ) {
				let otherId = this.getAttribute( 'data-validate-equal' );
				let other   = self.view.$el.find( `#${ otherId }` );
				let fail    = ( element.val().trim() !== other.val().trim() );
				if ( fail ) {
					valid = false;
					if ( element.prop( 'data-visited' ) && other.prop( 'data-visited' ) ) {
						
						let label1    = other[ 0 ].labels[ 0 ].innerText;
						let label2    = element[ 0 ].labels[ 0 ].innerText;
						validateError = T.tc( 'Form', 'Fields \'%s\' and \'%s\' must be identical.' ).format( trimColon( label1 ), trimColon( label2 ) );
						
						element.addClass( 'error' );
						other.addClass( 'error' );
					}
				} else {
					element.removeClass( 'error' );
					other.removeClass( 'error' );
				}
				
			}
		} );
		this.setValidateError( validateError );
		return valid;
	},
	
	setValidateError: function ( msg ) {
		msg = msg || '';
		this.$el.find( 'p.validation-errors' ).text( msg );
	},
	
	/**
	 * Resets the @ui.required object.
	 * @void
	 */
	resetRequired: function () {
		this.ui.required = this.view.$el.find( selectors.inputRequired );
	},
	
	/**
	 * Triggers before a view triggers it's submit.
	 * @void
	 */
	_submit: function () {
		
		this.destroyFeedback();
		
		if ( this._validate() ) {
			this.view.trigger( 'before:submit' );
			this.view.busy();
			
			this.view.submit();
		} else {
			if ( _.isFunction( this.view.disableSubmit ) ) {
				this.view.disableSubmit();
			}
			if ( _.isFunction( this.view.feedback ) ) {
				this.view.feedback();
			}
		}
	},
	
} );

function trimColon( text ) {
	return text.replace( /:$/, '' );
}

export default FormEnabler;
